/*
  Timeline tab pieces.
  Wave 1 (screen 10):
  - HandoverBanner        — red callout when the handover (end) date has passed on an active project, with
                            "Update end date" and "Put On Hold" (idea 5); amber note while On Hold
  - UpdateEndDateDialog   — small date dialog used by the banner
  Wave 2 (screen 10, idea 6):
  - TimelineControls      — zoom Week | Month | Quarter and "Group: Main task / Category / Building"
  The wave-1 empty state and the wave-2 bar view were replaced in wave 3 by TimelinePlan.jsx (PlanCard, the
  foundation Gantt) and removed.
*/
import { useEffect, useState } from 'react';
import { CalendarClock, ChevronDown, CirclePause, CirclePlay, Layers3, Loader2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate, toDateInputValue } from '@/lib/format';
import { Segmented } from '@/components/ui/segmented';
import { cn } from '@/lib/utils';
import { ActionMenu, Callout } from './ProjectUi';
import { isIncompleteDate } from './projectDetailModel';
import { TIMELINE_GROUPS, TIMELINE_ZOOMS } from './timelineModel';

const listText = (items = []) => {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

export function HandoverBanner({ handover, execution, openStages = 0, openTaskNames = [], canEdit, busy, onUpdateEndDate, onPutOnHold, onResume }) {
  if (!handover || handover.closed || handover.days <= 0) return null;
  const late = `${handover.days} day${handover.days === 1 ? '' : 's'}`;
  const leftParts = [];
  if (openStages) leftParts.push(`${openStages} delivery stage${openStages === 1 ? '' : 's'}`);
  if (openTaskNames.length) leftParts.push(openTaskNames.length > 2 ? `${openTaskNames.length} execution tasks` : listText(openTaskNames));
  const executionText = execution?.counted
    ? `Execution is ${execution.done} of ${execution.counted} done`
    : 'No execution tasks yet';
  const remaining = leftParts.length ? `; ${listText(leftParts)} ${leftParts.length === 1 && openStages === 1 && !openTaskNames.length ? 'is' : 'are'} still open.` : '.';

  if (handover.onHold) {
    return (
      <Callout
        tone="amber"
        icon={CirclePause}
        data-testid="handover-banner"
        actions={canEdit ? (
          <>
            <Button type="button" size="sm" variant="outline" onClick={onUpdateEndDate} disabled={busy}>
              <CalendarClock aria-hidden="true" />
              Update end date
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={onResume} disabled={busy}>
              <CirclePlay aria-hidden="true" />
              Set Active
            </Button>
          </>
        ) : null}
      >
        <b className="font-semibold text-ink">On hold.</b>
        {` Planned handover was ${formatDate(handover.endDate)} (${late} ago). ${executionText}${remaining}`}
      </Callout>
    );
  }

  return (
    <Callout
      tone="red"
      icon={TriangleAlert}
      role="alert"
      data-testid="handover-banner"
      actions={canEdit ? (
        <>
          <Button type="button" size="sm" variant="outline" onClick={onUpdateEndDate} disabled={busy} data-testid="handover-update-end-date">
            <CalendarClock aria-hidden="true" />
            Update end date
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onPutOnHold} disabled={busy} data-testid="handover-put-on-hold">
            <CirclePause aria-hidden="true" />
            Put On Hold
          </Button>
        </>
      ) : null}
    >
      <b className="font-semibold text-status-red-ink">Handover date {formatDate(handover.endDate)} passed {late} ago.</b>
      {` ${executionText}${remaining}`}
    </Callout>
  );
}

export function UpdateEndDateDialog({ open, onOpenChange, currentDate, onSave }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setValue(toDateInputValue(currentDate) || '');
      setError('');
    }
  }, [open, currentDate]);

  const submit = async (event) => {
    event.preventDefault();
    if (!value) return;
    if (isIncompleteDate(value)) {
      setError('Enter the full date — a 4-digit year from 1900.');
      return;
    }
    setSaving(true);
    try {
      const ok = await onSave(value);
      if (ok !== false) onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Update handover date</DialogTitle>
          <DialogDescription>
            {currentDate ? `Currently ${formatDate(currentDate)}.` : 'No handover date set yet.'} The new date is used for overdue checks.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project-end-date">New handover (end) date</Label>
            <Input
              id="project-end-date"
              type="date"
              value={value}
              onChange={(event) => { setValue(event.target.value); setError(''); }}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'project-end-date-error' : undefined}
              required
            />
            {error ? <p id="project-end-date-error" className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!value || saving} data-testid="save-end-date">
              {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              Save date
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ wave 2: zoom + grouping (idea 6) */

/** Zoom (Week | Month | Quarter) and grouping for the timeline toolbar. */
export function TimelineControls({ zoom, group, onZoomChange, onGroupChange, disabled = false, groupDisabled = false }) {
  const groupLabel = TIMELINE_GROUPS.find((entry) => entry.key === group)?.label || 'Main task';
  return (
    <div className={cn('flex flex-wrap items-center gap-2', disabled && 'pointer-events-none opacity-50')} aria-disabled={disabled || undefined}>
      <Segmented
        ariaLabel="Timeline zoom"
        value={zoom}
        onChange={onZoomChange}
        options={TIMELINE_ZOOMS.map((entry) => ({ key: entry.key, label: entry.label, testId: `timeline-zoom-${entry.key}` }))}
        testId="timeline-zoom"
      />
      <ActionMenu
        label="Group the timeline"
        align="end"
        trigger={(
          <Button type="button" variant="ghost" data-testid="timeline-group" disabled={disabled || groupDisabled} title={groupDisabled ? 'Grouping applies to execution tasks with dates' : undefined}>
            <Layers3 aria-hidden="true" />
            Group: {groupLabel}
            <ChevronDown aria-hidden="true" />
          </Button>
        )}
        items={TIMELINE_GROUPS.map((entry) => ({
          key: entry.key,
          label: entry.label,
          hint: entry.key === group ? 'Current' : undefined,
          testId: `timeline-group-${entry.key}`,
          onSelect: () => onGroupChange(entry.key),
        }))}
      />
    </div>
  );
}
